import _ from 'lodash';

import {IframeFeaturePoliciesEnum} from '../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {tryParseUrl} from '../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {allowedProtocols} from '../../../libs/shared/core/src/models/helpers/navigationHelpers';
import {ApplicationError} from '../../applications/bridge/v2/types/bridgeTypesV2';
import {ListApplicationCredentialsModel} from '../../models/api/applicationCredentialsModel';
import {ApplicationEntryPointActionFeaturesEnum} from '../../models/api/applicationEntryPointActionModel';
import {
  ApplicationEntryPointModel,
  ApplicationEntryPointTypesEnum,
} from '../../models/api/applicationEntryPointModel';
import {ApplicationModel, ApplicationServerModel} from '../../models/api/applicationModel';
import {ApplicationSettingsModel} from '../../models/api/applicationSettingsModel';
import {ServerAuthenticationStrategiesEnum} from '../../models/api/serverModel';
import {getJiraApplicationUid} from '../../settings/apps/constants';
import {computeJiraApplicationOriginError} from '../../settings/apps/helpers/jira';
import {LocalApplicationFeaturesEnum} from '../localApplicationHelpers';

/*
 * Constants.
 */

const applicationFeaturesToIframeFeaturePoliciesMap: {
  [K in ApplicationEntryPointActionFeaturesEnum]?: ReadonlyArray<IframeFeaturePoliciesEnum>;
} = {
  [ApplicationEntryPointActionFeaturesEnum.DEVICE_CAMERA]: [IframeFeaturePoliciesEnum.CAMERA],
  [ApplicationEntryPointActionFeaturesEnum.DEVICE_FULLSCREEN]: [IframeFeaturePoliciesEnum.FULLSCREEN],
  [ApplicationEntryPointActionFeaturesEnum.DEVICE_MICROPHONE]: [IframeFeaturePoliciesEnum.MICROPHONE],
  [ApplicationEntryPointActionFeaturesEnum.DEVICE_CLIPBOARD]: [
    IframeFeaturePoliciesEnum.CLIPBOARD_READ,
    IframeFeaturePoliciesEnum.CLIPBOARD_WRITE,
  ],
  [ApplicationEntryPointActionFeaturesEnum.DISPLAY_CAPTURE]: [IframeFeaturePoliciesEnum.DISPLAY_CAPTURE],
};

export const appIframePermissions = [
  // Allows the content to be treated as being from its normal origin.
  'allow-same-origin',
  // Allows the embedded browsing context to run javascript.
  'allow-scripts',
  // Allows forms to be submitted.
  'allow-forms',
  // Allows window to be open. The window will inherit the sandbox permissions.
  'allow-popups',
  // Allows usage of the Storage Access API.
  'allow-storage-access-by-user-activation',
].join(' ');

/*
 * Helpers.
 */

export function buildAppIframeFeaturePolicies(applicationEntryPoint: ApplicationEntryPointModel) {
  return _(applicationEntryPoint.action?.features)
    .map((f) => applicationFeaturesToIframeFeaturePoliciesMap[f])
    .compact()
    .flatten()
    .uniq()
    .value();
}

export function findApplicationAbsoluteUrl(application: ApplicationModel, urlOrPath: string) {
  // If the URL provided is already an absolute URL, return it.
  const absoluteUrl = tryParseUrl(urlOrPath);
  if (absoluteUrl) {
    if (!allowedProtocols.includes(absoluteUrl.protocol)) {
      throw new Error('This protocol is not allowed');
    }
    return absoluteUrl.href;
  }
  throw new ApplicationError('Invalid URL.');
}

export function computeServersToFeaturesMap(
  application: ApplicationModel | undefined,
): Map<string, Set<LocalApplicationFeaturesEnum>> {
  if (!application || !application.servers || application.servers.length === 0) {
    return new Map();
  }

  const serversToFeatures = new Map(
    application.servers.map((server) => [server.origin, new Set<LocalApplicationFeaturesEnum>()]),
  );

  // validate entry points
  application.entryPoints?.forEach((entryPoint) => {
    const serverOrigin = entryPoint.action?.origin;

    if (!serverOrigin) {
      return;
    }

    if (entryPoint.type === ApplicationEntryPointTypesEnum.INBOX_SIDE_PANEL) {
      serversToFeatures.get(serverOrigin)?.add(LocalApplicationFeaturesEnum.INBOX_SIDEPANEL);
    }

    if (entryPoint.type === ApplicationEntryPointTypesEnum.COMPOSER_TOOLBAR) {
      serversToFeatures.get(serverOrigin)?.add(LocalApplicationFeaturesEnum.COMPOSER_TOOLBAR);
    }
  });

  // create map and verify connectors
  const serverUidToOrigin = new Map(application.servers.map(({uid, origin}) => [uid, origin]));

  application.requests?.forEach(({serverUid}) => {
    const serverOrigin = serverUidToOrigin.get(serverUid);
    if (!serverOrigin) {
      return;
    }

    serversToFeatures.get(serverOrigin)?.add(LocalApplicationFeaturesEnum.CONNECTORS);
  });

  return serversToFeatures;
}

export function computeIsServerAuthRequired(
  server: ApplicationServerModel,
  featuresMap: ReturnType<typeof computeServersToFeaturesMap>,
) {
  if (server.type === ServerAuthenticationStrategiesEnum.NONE) {
    return false;
  }

  const features = featuresMap.get(server.origin);
  if (!features) {
    return false;
  }

  return features.size > 0;
}

export function computeAnyServerRequiresAuthentication(
  servers: ReadonlyArray<ApplicationServerModel>,
  credentials: ListApplicationCredentialsModel['credentials'],
) {
  const credentialsOriginSet = new Set(credentials.map((credential) => credential.origin));

  return servers.some(({origin}) => !credentialsOriginSet.has(origin));
}

/**
 * Given an application, compute the origin that requires reauthentication.
 */
export function computeAppOriginRequiresReauthentication(
  application?: ApplicationModel,
  applicationSettings?: ApplicationSettingsModel,
): string | undefined {
  if (!application) {
    return undefined;
  }

  switch (application.uid) {
    case getJiraApplicationUid():
      return computeJiraApplicationOriginError(application, applicationSettings);
    default:
      return undefined;
  }
}
